import { useSelectedOrg, useSettlementCreditDetails } from "@fero/hooks";
import { useTradeSalesStore } from "@fero/stores";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  FormControlLabel,
  Grid,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import type { BigDecimal } from "adl-gen/common";
import type { NominatedPurchaserDetailsView } from "adl-gen/ferovinum/app/api";
import type {
  Currency,
  TradeSaleSettlementMethod,
} from "adl-gen/ferovinum/app/db";
import type { OrgPurchaseRequestSummaryPreview } from "adl-gen/ferovinum/app/nompurchaser";
import type { SettlementCreditDetailsResp } from "adl-gen/ferovinum/app/settlementcredit";
import { FEROVINUM_CONTACT_URL } from "components/assets/url";
import { BaseWizardCard } from "components/library/widgets/wizard-card/base-wizard-card";
import { formatCurrencyValue } from "components/widgets/currency-renderer/currency-renderer";
import { Loading } from "components/widgets/loader/loader";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { ensure } from "utils/null-and-undefined-utils";

interface SettlementRadioOption {
  key: TradeSaleSettlementMethod;
  loading?: boolean;
  disabled: boolean;
  component: React.ReactNode;
}

interface SettlementOptionsCardProps {
  nominatedPurchaser: NominatedPurchaserDetailsView;
  summary: OrgPurchaseRequestSummaryPreview;
  settlementOption?: TradeSaleSettlementMethod;
  onSettlementOptionChange: (option: string) => void;
  disabled?: boolean;
}

export const SettlementOptionsCard: React.FC<SettlementOptionsCardProps> = ({
  nominatedPurchaser,
  summary,
  onSettlementOptionChange,
  settlementOption,
}) => {
  const isEditMode = useTradeSalesStore((state) => state.options.isEditMode);
  const { options, isLoading, isError } = useSettlementCreditOptions(
    nominatedPurchaser,
    summary,
  );
  const onChange = useCallback(
    (val) => onSettlementOptionChange(val),
    [onSettlementOptionChange],
  );

  // Once all options are loaded, default the first enabled option
  useEffect(() => {
    const firstEnabledOption = options.find(
      (option) => option && !option.disabled,
    );
    if (firstEnabledOption && !settlementOption) {
      onSettlementOptionChange(firstEnabledOption.key);
    }
  }, [options, settlementOption, onSettlementOptionChange]);

  const allOptionsDisabled =
    !isLoading &&
    (options.length === 0 || options.every((option) => option.disabled));
  if (isEditMode) {
    return (
      <BaseWizardCard
        title="Payment Details"
        subtitle="Please specify your preferred payment method."
        inputElement={
          <Alert severity="info">
            You cannot currently edit payment method in edit mode
          </Alert>
        }
      />
    );
  }

  return (
    <BaseWizardCard
      title="Payment Details"
      subtitle="Please specify your preferred payment method."
      inputElement={
        <Grid container spacing={2} direction={"column"} width={"100%"}>
          <Grid item xs={12}>
            <Stack spacing={2}>
              {allOptionsDisabled && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <WarningAmberOutlinedIcon color="warning" />
                  <Typography color="error">
                    No payment options are available for this sale. For further
                    assistance, please{" "}
                    <Link
                      color="error"
                      href={FEROVINUM_CONTACT_URL}
                      underline="always"
                    >
                      contact Ferovinum
                    </Link>
                    .
                  </Typography>
                </Stack>
              )}
              {isLoading ? (
                <Loading loading={isLoading} fullScreen={false} />
              ) : isError ? (
                <Typography color="error">
                  Failed to load settlement options. Please try again.
                </Typography>
              ) : (
                // Only render out options once loaded, first option is selected or all disabled
                (settlementOption || allOptionsDisabled) && (
                  <RadioGroup
                    value={settlementOption}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onChange(event.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    {options.map((option) => option.component)}
                  </RadioGroup>
                )
              )}
            </Stack>
          </Grid>
        </Grid>
      }
    />
  );
};

const useSettlementCreditOptions = (
  nominatedPurchaser: NominatedPurchaserDetailsView,
  summary: OrgPurchaseRequestSummaryPreview,
) => {
  const organisation = ensure(useSelectedOrg());
  const nominatedPurchaserCredit = useSettlementCreditDetails({
    currency: nominatedPurchaser.currency,
    counterpartyRef: {
      kind: "purchaser_np",
      value: nominatedPurchaser.nominatedPurchaserId,
    },
  });
  const organisationCredit = useSettlementCreditDetails({
    currency: organisation.value.currency,
    counterpartyRef: {
      kind: "organisation",
      value: organisation.id,
    },
  });
  const isLoading =
    nominatedPurchaserCredit.isLoading || organisationCredit.isLoading;
  const isError =
    nominatedPurchaserCredit.isError || organisationCredit.isError;

  const options = useMemo(() => {
    if (isError || isLoading) {
      return [];
    }

    // When auto accept on but not yet authorised we should disable purchaser credit as an option
    const autoAcceptNotAuthorised =
      nominatedPurchaser.autoAcceptTradeSale &&
      !nominatedPurchaser.tradeSaleAuthorised;
    return [
      buildPurchaserCreditOption(
        nominatedPurchaserCredit.data,
        nominatedPurchaser,
        Number(summary.invoiceTotal),
        autoAcceptNotAuthorised,
      ),
      buildOrganisationCreditOption(
        organisationCredit.data,
        Number(summary.outstandingBalance),
      ),
    ];
  }, [
    isError,
    isLoading,
    nominatedPurchaser,
    nominatedPurchaserCredit.data,
    organisationCredit.data,
    summary.invoiceTotal,
    summary.outstandingBalance,
  ]);

  return { options, isLoading, isError };
};

const buildCreditOption = (
  creditDetails: SettlementCreditDetailsResp | null,
  key: "nominatedPurchaserCredit" | "organisationCredit",
  label: string,
  name: string,
  amount: number,
  disabled?: boolean,
): SettlementRadioOption => {
  if (!creditDetails) return { key, component: null, disabled: true };

  const belowAvailableCredit =
    Number(creditDetails.creditLimitAvailable) - amount < 0;

  return {
    key,
    disabled: disabled || belowAvailableCredit,
    component: (
      <PaymentOptionItem
        key={key}
        optionKey={key}
        disabled={disabled || belowAvailableCredit}
        label={label}
        description="Order will be immediately processed."
        details={
          <CreditDetailsDisplay
            availableCredit={creditDetails.creditLimitAvailable}
            currency={creditDetails.limit.value.currency}
            name={name}
            amount={amount}
          />
        }
      />
    ),
  };
};

const buildPurchaserCreditOption = (
  creditDetails: SettlementCreditDetailsResp,
  nominatedPurchaser: NominatedPurchaserDetailsView,
  invoiceTotal: number,
  disabled: boolean,
): SettlementRadioOption =>
  buildCreditOption(
    creditDetails,
    "nominatedPurchaserCredit",
    `Settle with ${nominatedPurchaser.name}'s credit.`,
    nominatedPurchaser.name,
    invoiceTotal,
    disabled,
  );

const buildOrganisationCreditOption = (
  creditDetails: SettlementCreditDetailsResp,
  outstandingBalance: number,
): SettlementRadioOption =>
  buildCreditOption(
    creditDetails,
    "organisationCredit",
    "Settle with your company's credit.",
    "your company",
    outstandingBalance,
  );

const CreditDetailsDisplay = ({
  availableCredit,
  currency,
  name,
  amount,
}: {
  availableCredit: BigDecimal;
  currency: Currency;
  name: string;
  amount: number;
}) => (
  <Stack spacing={1}>
    <Typography variant="body2" color="text.secondary">
      Current credit balance for {name} is{" "}
      <strong>
        {formatCurrencyValue({ currency, value: availableCredit })}
      </strong>
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Balance will be reduced by{" "}
      <strong>{formatCurrencyValue({ currency, value: amount })}</strong>
    </Typography>
  </Stack>
);

const PaymentOptionItem = ({
  optionKey,
  label,
  description,
  details,
  disabled,
}: {
  optionKey: TradeSaleSettlementMethod;
  label: string;
  description: string;
  details: React.ReactNode;
  disabled?: boolean;
}) => (
  <FormControlLabel
    value={optionKey}
    control={<Radio />}
    disabled={disabled}
    componentsProps={{ typography: { width: "100%" } }}
    label={
      <Grid container>
        <Grid item xs={6}>
          <Stack spacing={1}>
            <Typography variant="body1">{label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={6}>
          {details}
        </Grid>
      </Grid>
    }
  />
);
